import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react";
import {
  API_ROUTES,
  BJ,
  DEFAULT_SKIN_ID,
  allowedActions,
  handValue,
  roundResultKey,
  type BjAction,
  type BjActionContext,
  type BjCard,
  type BjGameUpdate,
  type BjHandOutcome,
  type BjLeaderboardRow,
  type BjPublicCard,
  type BjPublicPlayer,
  type BjWallet,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useSession } from "../session/SessionProvider.js";
import { BjHandView } from "../games/blackjack/BjCards.js";
import { ShopPanel } from "../games/shop/ShopPanel.js";
import { useBlackjackSocket } from "../games/blackjack/useBlackjackSocket.js";
import { CasinoOnPageAdmin } from "../components/admin/CasinoAdminTools.js";
import "../styles/blackjack.css";
import "../styles/skins.css";
import "../styles/shop.css";

const ACTION_LABEL: Record<BjAction, string> = {
  hit: "HIT",
  stand: "STAND",
  double: "DOUBLE",
  split: "SPLIT",
  insurance: "INSURANCE",
  surrender: "SURRENDER",
};

const RESULT_LABEL: Record<BjHandOutcome["result"], string> = {
  win: "wygrana",
  lose: "przegrana",
  push: "remis",
  blackjack: "blackjack",
  busted: "bust",
  surrender: "poddanie",
};

const SEAT_SLOTS = Array.from({ length: BJ.MAX_SEATS }, (_, index) => index);

/** Equal arc-length points on the lower horseshoe. Bet circle sits on the curve. */
function seatArcAnchors(count: number): CSSProperties[] {
  const start = (192 * Math.PI) / 180;
  const end = (348 * Math.PI) / 180;
  const rx = 45;
  const ry = 31.5;
  const cx = 50;
  const cy = 46;
  // D-table is wider than tall; weight X so pixel spacing along the rail is even.
  const aspect = 1.72;
  const samples = 240;
  const pts: Array<{ x: number; y: number; dist: number }> = [];
  let dist = 0;
  for (let i = 0; i <= samples; i += 1) {
    const t = start + ((end - start) * i) / samples;
    const x = cx + rx * Math.cos(t);
    const y = cy + ry * Math.sin(t);
    if (i > 0) {
      const prev = pts[i - 1];
      if (prev) dist += Math.hypot((x - prev.x) * aspect, y - prev.y);
    }
    pts.push({ x, y, dist });
  }
  const step = dist / (count - 1);
  return Array.from({ length: count }, (_, index) => {
    const target = step * index;
    let i = 1;
    while (i < pts.length && (pts[i]?.dist ?? 0) < target) i += 1;
    const b = pts[i] ?? pts[pts.length - 1];
    const a = pts[i - 1] ?? b;
    if (!a || !b) {
      return { "--bj-left": "50%", "--bj-bottom": "8%" } as CSSProperties;
    }
    const span = b.dist - a.dist;
    const u = span > 0 ? (target - a.dist) / span : 0;
    const x = a.x + (b.x - a.x) * u;
    const y = a.y + (b.y - a.y) * u;
    return {
      "--bj-left": `${x.toFixed(2)}%`,
      "--bj-bottom": `${y.toFixed(2)}%`,
    } as CSSProperties;
  });
}

const SEAT_ARC_STYLES = seatArcAnchors(BJ.MAX_SEATS);

function asCards(hand: BjPublicCard[]): BjCard[] {
  const cards: BjCard[] = [];
  for (const card of hand) {
    if ("suit" in card) cards.push({ suit: card.suit, value: card.value });
  }
  return cards;
}

function visibleValue(hand: BjPublicCard[]): number | null {
  const cards = asCards(hand);
  if (cards.length === 0) return null;
  return handValue(cards);
}

function phaseCopy(table: BjGameUpdate): string {
  if (table.phase === "waiting") return "Czekamy na graczy";
  if (table.phase === "betting") return "Czas na zakłady";
  if (table.phase === "playing") return "Gra";
  return `${table.dealerName} dobiera`;
}

function formatHms(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function useCountdown(deadline: number | null): number {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (deadline === null) {
      setLeft(0);
      return;
    }
    const tick = () => setLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [deadline]);
  return left;
}

function useLeaderboard(refreshToken: number) {
  const [rows, setRows] = useState<BjLeaderboardRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiRequest<BjLeaderboardRow[]>(API_ROUTES.blackjack.leaderboard)
        .then((next) => {
          if (!cancelled) {
            setRows(next);
            setError(null);
          }
        })
        .catch((cause: unknown) => {
          if (!cancelled) setError(errorMessage(cause));
        });
    };
    load();
    const id = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refreshToken]);

  return { rows, error };
}

function useWallet(enabled: boolean, refreshToken: number) {
  const [wallet, setWallet] = useState<BjWallet | null>(null);

  useEffect(() => {
    if (!enabled) {
      setWallet(null);
      return;
    }
    let cancelled = false;
    apiRequest<BjWallet>(API_ROUTES.blackjack.wallet)
      .then((next) => {
        if (!cancelled) setWallet(next);
      })
      .catch(() => {
        // Socket session will fill this in if the HTTP call races a logout.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, refreshToken]);

  return wallet;
}

export function BlackjackPage() {
  const { user, restoring } = useSession();
  const enabled = Boolean(user) && !restoring;
  const {
    table,
    session,
    chat,
    notice,
    connected,
    error,
    walletEpoch,
    join,
    leave,
    placeBet,
    act,
    sendChat,
    claimBonus,
  } = useBlackjackSocket(enabled);
  const [adminTick, setAdminTick] = useState(0);
  const board = useLeaderboard(walletEpoch + adminTick);
  const wallet = useWallet(enabled, walletEpoch);
  const [betDraft, setBetDraft] = useState(String(BJ.DEFAULT_BET));
  const [chatDraft, setChatDraft] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [rankOpen, setRankOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [equippedOverride, setEquippedOverride] = useState<string | null>(null);
  const chatEnd = useRef<HTMLDivElement | null>(null);
  const bettingLeft = useCountdown(table.phase === "betting" ? table.bettingEndsAt : null);
  const turnLeft = useCountdown(table.phase === "playing" ? table.turnEndsAt : null);
  const bonusLeft = useCountdown(session?.canClaim ? null : (session?.nextAvailableAt ?? wallet?.nextBonusAt ?? null));

  const userId = user?.id ?? 0;
  const me = table.players[String(userId)];
  const waitingMe = table.waitingList.find((entry) => entry.userId === userId);
  const seated = Boolean(me);
  const chips = me?.chips ?? waitingMe?.chips ?? session?.chips ?? wallet?.chips ?? 0;
  const keys = me?.keys ?? waitingMe?.keys ?? session?.keys ?? wallet?.keys ?? 0;
  const myTurn =
    seated &&
    table.phase === "playing" &&
    table.currentTurn?.userId === userId;
  const actions = useMemo(() => {
    if (!me || !table.currentTurn || table.currentTurn.userId !== userId) return [];
    const hi = table.currentTurn.handIndex;
    const hand = me.hands[hi] ?? [];
    const ctx: BjActionContext = {
      phase: table.phase,
      isYourTurn: true,
      hand: asCards(hand),
      handStatus: me.handStatus[hi] ?? "active",
      hasActed: me.hasActedOnHand[hi] ?? false,
      chips: me.chips,
      bet: me.bets[hi] ?? 0,
      splitCount: me.splitCount,
      insuranceOpen: table.insuranceOpen,
      insuranceBet: me.insuranceBet,
      primaryBet: me.bets[0] ?? 0,
    };
    return allowedActions(ctx);
  }, [me, table, userId]);

  const slots = useMemo(() => {
    const byIndex: Array<BjPublicPlayer | null> = Array.from({ length: BJ.MAX_SEATS }, () => null);
    const placed = new Set<number>();
    for (const player of Object.values(table.players)) {
      if (player.seatIndex >= 0 && player.seatIndex < BJ.MAX_SEATS && !byIndex[player.seatIndex]) {
        byIndex[player.seatIndex] = player;
        placed.add(player.userId);
      }
    }
    let cursor = 0;
    for (const player of Object.values(table.players)) {
      if (placed.has(player.userId)) continue;
      while (cursor < BJ.MAX_SEATS && byIndex[cursor]) cursor += 1;
      if (cursor < BJ.MAX_SEATS) {
        byIndex[cursor] = player;
        cursor += 1;
      }
    }
    return byIndex;
  }, [table.players]);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ block: "end" });
  }, [chat.length]);

  useEffect(() => {
    if (!shopOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShopOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shopOpen]);

  if (restoring) {
    return <p className="hub-hint">Ładowanie…</p>;
  }

  if (!user) {
    return (
      <Card title="Blackjack" icon="♠️">
        <p className="hub-hint">Zaloguj się w belce na górze, żeby usiąść przy stole.</p>
      </Card>
    );
  }

  function submitChat(event: FormEvent) {
    event.preventDefault();
    const text = chatDraft.trim();
    if (!text) return;
    sendChat(text);
    setChatDraft("");
  }

  const tableFull = table.seatedCount >= BJ.MAX_SEATS && !seated;
  const canClaim = (session?.canClaim ?? wallet?.canClaimBonus ?? false) && connected;
  const canJoinEmpty = connected && !seated && !tableFull;
  const mySeatIndex = me?.seatIndex ?? null;
  const bonusLabel = canClaim
    ? `Bonus +${BJ.BONUS_AMOUNT}`
    : bonusLeft > 0
      ? `Bonus in ${formatHms(bonusLeft * 1000)}`
      : `Bonus +${BJ.BONUS_AMOUNT}`;

  const phaseBits = table.dealerResult
    ? []
    : [
        phaseCopy(table),
        table.phase === "betting" && bettingLeft > 0 ? `${bettingLeft}s` : null,
        myTurn && turnLeft > 0 ? `twoja tura ${turnLeft}s` : null,
        table.insuranceOpen ? "ubezpieczenie" : null,
      ].filter(Boolean);
  const dealerValue = visibleValue(table.dealerHand);
  const mySkinId =
    equippedOverride ?? me?.skinId ?? session?.equippedSkin ?? wallet?.equippedSkin ?? DEFAULT_SKIN_ID;

  return (
    <>
    <div className="bj-page">
      {chatOpen ? (
        <button
          type="button"
          className="bj-chat-overlay"
          aria-label="Zamknij chat"
          onClick={() => setChatOpen(false)}
        />
      ) : null}

      <aside className={`bj-chat ${chatOpen ? "bj-chat--open" : ""}`}>
        <div className="bj-chat__head">
          <h3>Chat</h3>
          <button type="button" className="bj-chat__close" onClick={() => setChatOpen(false)}>
            ✕
          </button>
        </div>
        <ul className="bj-chat__log">
          {chat.map((message, index) => {
            const mine = !message.isDealer && message.user === user.username;
            const lineClass = [
              "bj-chat__line",
              message.isDealer ? "bj-chat__line--dealer" : "",
              mine ? "bj-chat__line--mine" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <li key={`${message.ts}-${index}`} className={lineClass}>
                <strong>{message.user}</strong>
                <span>{message.text}</span>
              </li>
            );
          })}
          <div ref={chatEnd} />
        </ul>
        <form className="bj-chat__form" onSubmit={submitChat}>
          <input
            value={chatDraft}
            onChange={(event) => setChatDraft(event.target.value)}
            maxLength={BJ.MAX_CHAT_LENGTH}
            placeholder="Napisz…"
            autoComplete="off"
          />
          <Button type="submit" size="sm" variant="primary">
            Wyślij
          </Button>
        </form>
      </aside>

      <div className="bj-main">
        <div className="bj-toolbar">
          <div className="bj-hud">
            <div className="bj-hud__stat">
              Żetony: <strong>{chips}</strong>
            </div>
            <div className="bj-hud__stat">
              Klucze: <strong>{keys}</strong>
            </div>
            <Button size="sm" variant="primary" className="bj-bonus" disabled={!canClaim} onClick={claimBonus}>
              {bonusLabel}
            </Button>
            <Button size="sm" className="bj-shop" onClick={() => setShopOpen(true)}>
              Sklep
            </Button>
            {seated ? (
              <Button size="sm" className="bj-join" onClick={leave}>
                Opuść stół
              </Button>
            ) : (
              <Button size="sm" variant="primary" className="bj-join" disabled={tableFull || !connected} onClick={() => join()}>
                Dołącz
              </Button>
            )}
            <button
              type="button"
              className={`bj-rank-toggle ${rankOpen ? "bj-rank-toggle--on" : ""}`}
              onClick={() => setRankOpen((open) => !open)}
              aria-expanded={rankOpen}
            >
              🏆
            </button>
            <button type="button" className="bj-chat-toggle" onClick={() => setChatOpen(true)}>
              💬
            </button>
          </div>
        </div>

        <ul className="bj-waiting">
          {table.waitingList.length > 0 ? (
            <>
              <li className="bj-waiting__label">Poczekalnia</li>
              {table.waitingList.map((entry) => (
                <li key={entry.userId}>
                  {entry.name}
                </li>
              ))}
            </>
          ) : null}
        </ul>

        <div className="bj-status">
          {notice ? <p className="bj-notice">{notice}</p> : null}
          {error ? <p className="hub-error">{error}</p> : null}
          {!connected && enabled ? <p className="hub-hint">Łączenie ze stołem…</p> : null}
        </div>

        <div className="bj-felt">
          <div className="bj-felt__rail">
            <div className="bj-felt__cloth">
              <div className="bj-felt__top">
                <p className={phaseBits.length > 0 ? "bj-phase" : "bj-phase bj-phase--empty"}>
                  {phaseBits.length > 0 ? phaseBits.join(" · ") : "\u00a0"}
                </p>
                <div className="bj-dealer">
                  <p className="bj-dealer__name">Krupier {table.dealerName}</p>
                  <div className="bj-dealer__cards">
                    {table.phase !== "waiting" ? (
                      <BjHandView cards={table.dealerHand} skinId={mySkinId} />
                    ) : null}
                    {table.dealerResult ? (
                      <p className="bj-dealer__result">
                        {table.dealerResult.busted
                          ? "bust"
                          : table.dealerResult.blackjack
                            ? "blackjack"
                            : `${table.dealerResult.value}`}
                      </p>
                    ) : dealerValue !== null ? (
                      <p className="bj-hand-value">{dealerValue}</p>
                    ) : (
                      <p className="bj-hand-value bj-hand-value--empty">{"\u00a0"}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bj-arc">
                {SEAT_SLOTS.map((slot) => (
                  <SeatSlot
                    key={slot}
                    slot={slot}
                    seat={slots[slot] ?? null}
                    table={table}
                    isYou={slot === mySeatIndex}
                    viewingSkinId={mySkinId}
                    canJoin={canJoinEmpty}
                    onJoin={join}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={`bj-rank bj-rank--hud ${rankOpen ? "bj-rank--open" : ""}`}>
          <RankList rows={board.rows} error={board.error} />
        </div>

        <div className="bj-actions">
          {seated && table.phase === "betting" && !me?.hasBet ? (
            <div className="bj-bet">
              <label>
                Zakład (min. {BJ.MIN_BET})
                <input
                  type="number"
                  min={BJ.MIN_BET}
                  max={Math.min(BJ.MAX_BET, chips)}
                  value={betDraft}
                  onChange={(event) => setBetDraft(event.target.value)}
                />
              </label>
              <Button type="button" variant="primary" onClick={() => placeBet(Number(betDraft))}>
                Postaw
              </Button>
            </div>
          ) : null}
          {myTurn ? (
            <div className="bj-action-row">
              {actions.map((action) => (
                <button
                  key={action}
                  type="button"
                  className={`bj-act bj-act--${action}`}
                  onClick={() => act(action)}
                >
                  {ACTION_LABEL[action]}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      {shopOpen ? (
        <div
          className="shop-modal-root"
          onClick={(event) => {
            if (event.target === event.currentTarget) setShopOpen(false);
          }}
        >
          <ShopPanel
            variant="modal"
            onClose={() => setShopOpen(false)}
            initialChips={chips}
            initialKeys={keys}
            onWalletChange={(_chips, _keys, equipped) => {
              if (equipped) setEquippedOverride(equipped);
            }}
          />
        </div>
      ) : null}
    </div>
    <CasinoOnPageAdmin onChanged={() => setAdminTick((n) => n + 1)} />
    </>
  );
}

function RankList({ rows, error }: { rows: BjLeaderboardRow[]; error: string | null }) {
  return (
    <>
      <h3>Ranking</h3>
      {error ? <p className="hub-error">{error}</p> : null}
      {rows.length === 0 && !error ? (
        <p className="hub-hint">Jeszcze nikt nie usiadł.</p>
      ) : (
        <ol className="bj-list bj-list--rank">
          {rows.map((row) => (
            <li key={row.username}>
              <span>{row.username}</span>
              <strong>{row.maxChips}</strong>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

function SeatSlot({
  slot,
  seat,
  table,
  isYou,
  viewingSkinId,
  canJoin,
  onJoin,
}: {
  slot: number;
  seat: BjPublicPlayer | null;
  table: BjGameUpdate;
  isYou: boolean;
  viewingSkinId: string;
  canJoin: boolean;
  onJoin: (slot?: number) => void;
}) {
  const arcStyle = SEAT_ARC_STYLES[slot];

  if (!seat) {
    return (
      <button
        type="button"
        data-slot={slot}
        data-hands="0"
        className="bj-seat bj-seat--empty"
        style={arcStyle}
        disabled={!canJoin}
        onClick={() => onJoin(slot)}
      >
        <span className="bj-seat__name">Wolne</span>
        <span className="bj-spot" />
      </button>
    );
  }

  const isTurn = table.currentTurn?.userId === seat.userId;
  const waitingBet = table.phase === "betting" && !seat.hasBet;
  const handCount = Math.max(1, seat.hands.length);
  const className = [
    "bj-seat",
    isYou ? "bj-seat--you" : "",
    isTurn ? "bj-seat--turn" : "",
    waitingBet ? "bj-seat--waiting-bet" : "",
    handCount > 1 ? "bj-seat--split" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const betLabel = seat.bets.filter((bet) => bet > 0).join(" / ") || "—";

  return (
    <div data-slot={slot} data-hands={handCount} className={className} style={arcStyle}>
      <div className="bj-seat__hands">
        {seat.hands.map((hand, index) => {
          const active = isTurn && table.currentTurn?.handIndex === index;
          const outcome = table.roundResults[roundResultKey(seat.userId, index)];
          const value = visibleValue(hand);
          const resultClass = outcome ? `bj-result bj-result--${outcome.result}` : "";
          return (
            <div
              key={index}
              className={active ? "bj-seat__hand bj-seat__hand--active" : "bj-seat__hand"}
            >
              <BjHandView cards={hand} skinId={isYou ? viewingSkinId : seat.skinId} />
              {outcome ? (
                <p className={resultClass}>
                  {value !== null ? `${value} · ` : ""}
                  {RESULT_LABEL[outcome.result]}
                </p>
              ) : value !== null ? (
                <p className="bj-hand-value">{value}</p>
              ) : null}
            </div>
          );
        })}
      </div>
      <p className="bj-seat__name" title={seat.name}>
        {seat.name}
      </p>
      <div className="bj-spot">{betLabel === "—" ? "" : betLabel}</div>
    </div>
  );
}
